import React, { useContext, useEffect, useState } from 'react';
import { ApiTypesEnum, FindCarrierTypeEnum, LovDTO, LovWithoutIdDTO } from '@api/ringil-api.v2';
import { Carrier } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { ApiContext } from '@api/api.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';

interface ComponentProps {
  values: Carrier[];
  onSelect: (values: Carrier[]) => void;
}

export const DachserProductTerms: React.FC<ComponentProps> = ({
                                                                values,
                                                                onSelect,
                                                              }) => {
  const { tr } = useTranslationLgs();
  const [apiTypeValues, setApiTypeValues] = useState<LovDTO[]>([]);
  const { lovCarrierApiTypeValues, lovDeliveryTerm } = useContext(ApiContext);
  const [deliveryTerms, setDeliveryTerms] = useState<LovWithoutIdDTO[]>([]);
  const [isDachser, setIsDachser] = useState<boolean>(false);
  const [dachserCarriers, setDachserCarriers] = useState<Carrier[]>([]);
  const [deliveryTermsCarrierIds, setDeliveryTermsCarrierIds] = useState<string[]>([]);

  useEffect(() => {
    const hasDachserApi = Array.isArray(values) && values.some(carrier =>
      Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.DACHSER),
    );
    setIsDachser(hasDachserApi);
    const dachserCarriers = Array.isArray(values)
      ? values.filter(carrier =>
        Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.DACHSER),
      )
      : [];
    setDachserCarriers(dachserCarriers);

    if (hasDachserApi) {
      if (!apiTypeValues || apiTypeValues.length === 0) {
        lovCarrierApiTypeValues(ApiTypesEnum.DACHSER, values => {
          setApiTypeValues(values);
        });
      }

      const uniqueCarrierIds = Array.from(new Set(
        dachserCarriers.flatMap(carrier =>
          carrier.carrierType === FindCarrierTypeEnum.CARRIER
            ? [carrier.id]
            : Array.isArray(carrier.carrierIds)
              ? carrier.carrierIds
              : [],
        ),
      ));

      if (uniqueCarrierIds && uniqueCarrierIds.length > 0 && uniqueCarrierIds.length !== deliveryTermsCarrierIds.length && uniqueCarrierIds.some(id => !deliveryTermsCarrierIds.includes(id))) {
        lovDeliveryTerm(
          {
            carriersIds: uniqueCarrierIds,
          },
          values => {
            setDeliveryTerms(values[uniqueCarrierIds[0]]);
            setDeliveryTermsCarrierIds(uniqueCarrierIds);
          },
        );
      }
    }
  }, [values]);

  useEffect(() => {
    if (dachserCarriers && dachserCarriers.length > 0 && deliveryTerms?.length > 0 && apiTypeValues?.length > 0) {
      const needsUpdate = dachserCarriers.some(
        carrier => !carrier._dachserProductTypeCode || !carrier._dachserDeliveryTermCode,
      );

      if (needsUpdate) {
        const updated = dachserCarriers.map(carrier => ({
          ...carrier,
          _dachserProductTypeCode: carrier._dachserProductTypeCode || dachserCarriers[0]._dachserProductTypeCode || apiTypeValues[0].id,
          _dachserDeliveryTermCode: carrier._dachserDeliveryTermCode || dachserCarriers[0]._dachserDeliveryTermCode || 'FREE_DELIVERED',
        }));

        onSelect(updated);
      }
    }
  }, [dachserCarriers, deliveryTerms, apiTypeValues]);

  const handleApiProductSelect = (_dachserProductTypeCode: string) => {
    const updatedValue = dachserCarriers.map(item => {
      return {
        ...item,
        _dachserProductTypeCode,
      };
    });

    onSelect(updatedValue);
  };

  const handleApiSpecificTermsSelect = (_dachserDeliveryTermCode: string) => {
    const updatedValue = dachserCarriers.map(item => {
      return {
        ...item,
        _dachserDeliveryTermCode,
      };
    });

    onSelect(updatedValue);
  };

  return (
    <>
      {isDachser && (
        <div className="p-4 rounded-lg bg-base-tertiary">
          <Field
            label={tr('DachserProductTerms.dachserProduct', 'Dachser - select product')}
            required
          >
            <Dropdown
              options={apiTypeValues}
              optionLabel="name"
              optionValue="id"
              value={dachserCarriers[0]._dachserProductTypeCode || (apiTypeValues && apiTypeValues[0]?.id)}
              onChange={e => handleApiProductSelect(e.value)}
              style={{ width: '100%' }}
              placeholder={tr('DachserProductTerms.dachserProductPlaceholder', 'Select product')}
            />
          </Field>
          <Field
            label={tr('DachserProductTerms.dachserSpecificTerms', 'Dachser - select terms')}
            required
          >
            <Dropdown
              options={deliveryTerms}
              optionLabel="name"
              optionValue="code"
              value={dachserCarriers[0]._dachserDeliveryTermCode || 'FREE_DELIVERED'}
              onChange={e => handleApiSpecificTermsSelect(e.value)}
              style={{ width: '100%' }}
              placeholder={tr('DachserProductTerms.dachserSpecificTermsPlaceholder', 'Select product terms')}
            />
          </Field>
        </div>
      )}
    </>
  );
};